import React, { useState, useEffect, useCallback, useRef } from 'react';
import { INITIAL_STATE_BOOKING_HISTORY_DATA } from '../../../../utils/bookingChartsUtils';
import api from '../../../../services/api';
import '../../../../stylesheets/stylesComponents/bookingStatistics.scss';
import BookingStatisticsPlaceHolder from './BookingStatisticsPlaceholder';
import BookingTextStatistics from './BookingTextStatistics';
import BookingChart from './BookingChart';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import { getYearPeriod } from '../../../../utils/formatDates';
import endpoints from '../../../../utils/commonVariables/endpoints';
import { formatCancelledAndCommited } from '../../../../utils/usefulFunctions';
import Card from '../../../../muiComponents/Card';
import { useHotelOwner } from '../../../../contexts/HotelOwnerContext';
import { getCSSVariable } from '../../../../stylesheets/cssVariablesUtils';
import { Typography } from '@mui/material';
import useTranslate from '../../../../customHooks/useTranslate';
import useLanguage from '../../../../customHooks/useLanguage';
import { useBookingList } from '../../../contextsProperty/ContextBookings';
import Button from '../../../../muiComponents/Button';
import {
    BookingListViewType,
    BookingStatusColor,
} from '../../../../types/booking/bookingListTypes';
import { commonIcons } from '../../../../utils/commonVariables/commonIcons';

function BookingStatisticsSection() {
    const translate = useTranslate();
    const language = useLanguage();
    const { ownerData } = useHotelOwner();
    const { handleChangeVisibleView } = useBookingList();
    const [bookingsData, setBookingsData] = useState([]);

    const [chartData, setChartData] = useState({
        labels: bookingsData.map(data =>
            translate(data.month)?.substring(0, 1)
        ),
        datasets: [
            {
                label: translate('Bookings'),
                data: INITIAL_STATE_BOOKING_HISTORY_DATA.map(
                    data => data.bookingCount
                ),
                backgroundColor: getCSSVariable('primaryBrigth'),
            },
        ],
    });

    const [isLoadingCharts, setIsLoadingCharts] = useState(true);
    const componentMounted = useRef(true);

    const getBookingData = useCallback(async () => {
        const period = getYearPeriod();
        const endpoint = endpoints.bookingStats(period.start, period.end);

        try {
            const { data } = await api.get(endpoint);
            const newData = formatCancelledAndCommited(
                data,
                INITIAL_STATE_BOOKING_HISTORY_DATA
            );
            if (!componentMounted.current) return null;

            setChartData(prevState => {
                return {
                    ...prevState,
                    labels: newData.map(it =>
                        translate(it.month)?.substring(0, 1)
                    ),
                    datasets: [
                        {
                            label: 'Cancelled',
                            data: newData.map(
                                item => item.cancelledBookingCount
                            ),
                            backgroundColor: BookingStatusColor.CANCELLED,
                        },
                        {
                            label: 'Commited',
                            data: newData.map(
                                item => item.commitedBookingCount
                            ),
                            backgroundColor: getCSSVariable('primaryBrigth'),
                        },
                    ],
                };
            });
            setBookingsData(newData);
            setIsLoadingCharts(false);
        } catch (error) {
            console.log(error);
        }
    }, [translate]);

    useEffect(() => {
        getBookingData();
    }, [getBookingData, language]);

    useEffect(() => {
        return () => {
            componentMounted.current = false;
        };
    }, []);

    return (
        <>
            <div className='page-title__container margin-bottom10'>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant='h1'>
                        {translate('bookings')}
                    </Typography>
                </div>
                <div className='flex gap-10'>
                    <Button
                        onClick={() =>
                            handleChangeVisibleView(BookingListViewType.TABLE)
                        }
                        startIcon={commonIcons.LIST}
                        color='secondary'
                    >
                        {translate('list')}
                    </Button>
                    <Button
                        onClick={() =>
                            handleChangeVisibleView(
                                BookingListViewType.CALENDAR
                            )
                        }
                        startIcon={commonIcons.CALENDAR}
                        color='secondary'
                    >
                        {translate('calendar')}
                    </Button>
                </div>
            </div>
            <Card className={`booking-statistics__container`}>
                {isLoadingCharts ? (
                    <BookingStatisticsPlaceHolder />
                ) : (
                    <>
                        <div>
                            <SectionTitle
                                variant='h3'
                                title={translate('booking_stats')}
                                className='booking-charts__title'
                            />
                            <p className='booking-charts__subtitle'>{`${translate(
                                'past_12_months'
                            )}`}</p>
                        </div>
                        <div className='booking-statistics__content'>
                            <BookingChart
                                containerClassName='booking-page__booking-container'
                                chartData={chartData}
                                bookingsData={bookingsData}
                            />
                            <BookingTextStatistics
                                bookingsData={bookingsData}
                                currency={ownerData.currency}
                            />
                        </div>
                    </>
                )}
            </Card>
        </>
    );
}

export default BookingStatisticsSection;
